"use client";

import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import type React from "react";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { WASH_HOVER } from "./box-classes.ts";
import { Icon } from "./icon";

export function Accordion(
  props: AccordionPrimitive.Root.Props,
): React.ReactElement {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

export function AccordionItem({
  className,
  separator = true,
  ...props
}: AccordionPrimitive.Item.Props & Omittable<{
  /**
   * The hairline under the item (the last item never draws one). Off for a
   * list whose rows are already separated by their own rhythm — a compact
   * roster of `size="sm"` triggers reads as one column, not stacked panels.
   */
  separator?: boolean;
}>): React.ReactElement {
  return (
    <AccordionPrimitive.Item
      className={cn(separator && "border-b last:border-b-0", className)}
      data-slot="accordion-item"
      {...props}
    />
  );
}

export const accordionTriggerVariants = cva(
  // The chevron trails the label rather than pinning to the right edge:
  // that edge is where consumers put hover actions and row chrome.
  "flex flex-1 cursor-pointer items-start justify-start gap-1 rounded-md text-left font-medium text-sm outline-none transition-all focus-visible:ring-[3px] focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-64",
  {
    variants: {
      /**
       * Row height. `default` is the FAQ rung (16px insets, ~52px row).
       * `sm` is the roster rung: a 40px row on 8px insets, for a list of
       * titles the reader scans before opening one.
       */
      size: {
        default: "py-4",
        sm: "min-h-10 items-center py-2",
      },
      indicator: {
        flip: "data-panel-open:*:data-[slot=accordion-indicator]:rotate-180",
        disclosure: "data-panel-open:*:data-[slot=accordion-indicator]:rotate-90",
      },
      chrome: {
        wash: `h-8 min-h-8 flex-none items-center -ms-2 px-2 py-0 ${WASH_HOVER}`,
      },
    },
    defaultVariants: {
      indicator: "flip",
      size: "default",
    },
  },
);

export function AccordionTrigger({
  className,
  children,
  indicator = "flip",
  size,
  chrome,
  ...props
}: AccordionPrimitive.Trigger.Props & Omittable<{
  /** Row height — see `accordionTriggerVariants`. */
  size?: VariantProps<typeof accordionTriggerVariants>["size"];
  chrome?: VariantProps<typeof accordionTriggerVariants>["chrome"];
  /**
   * `flip` — the chevron points down and flips up while open (stacked
   * always-visible rows). `disclosure` — it rests pointing right and rotates
   * down while open: the closed-state affordance for sections that start
   * collapsed, where "there is more here" reads as `>`.
   */
  indicator?: VariantProps<typeof accordionTriggerVariants>["indicator"];
}>): React.ReactElement {
  const chevron = indicator === "disclosure" ? "chevron-right" : "chevron-down";
  return (
    // The header grows to fill whatever row it sits in, so the trigger's
    // press target runs from the label to the row chrome a consumer seats
    // beside it; in a stacked accordion the growth is a no-op.
    <AccordionPrimitive.Header className="flex min-w-0 flex-1">
      <AccordionPrimitive.Trigger
        className={cn(accordionTriggerVariants({ chrome, indicator, size }), className)}
        data-slot="accordion-trigger"
        {...props}
      >
        {children}
        <Icon
          className={cn(
            "pointer-events-none size-4 shrink-0 opacity-80 transition-transform duration-base ease-in-out",
            // A top-aligned row seats the chevron on the label's first line;
            // a centered one is already on it.
            size !== "sm" && chrome !== "wash" && "translate-y-0.5",
          )}
          data-slot="accordion-indicator"
          name={chevron}
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

export function AccordionPanel({
  className,
  children,
  padding = "default",
  ink,
  ...props
}: AccordionPrimitive.Panel.Props & Omittable<{
  /**
   * The gutter under the panel's content. `default` is the FAQ rung (16px),
   * the breath between one answer and the next title. `none` is for a panel
   * whose content carries its own rhythm — a recessed band, a roster on the
   * compact trigger rung — where the gutter would double what is already
   * there.
   */
  padding?: "default" | "none";
  ink?: "inherit";
}>): React.ReactElement {
  return (
    <AccordionPrimitive.Panel
      // `-mx-1` + `px-1` widen the clip box so a focus ring at the content edge is not sliced; `clip`, never `hidden`, which would make the panel a scroll container and scroll content under the trigger.
      className={cn(
        "-mx-1 h-(--accordion-panel-height) overflow-clip text-sm transition-[height] duration-base ease-in-out data-ending-style:h-0 data-starting-style:h-0",
        ink !== "inherit" && "text-muted-foreground",
      )}
      data-slot="accordion-panel"
      {...props}
    >
      <div className={cn("px-1 pt-0", padding === "default" && "pb-4", className)}>
        {children}
      </div>
    </AccordionPrimitive.Panel>
  );
}
